import { effect } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { applyUrlState } from './share';
import { services } from '../app';
import { CAMERA_HELP, refusal } from '../data/camerahelp';
import { camHelp, cameraHelpCanLook, cameraHelpDebug, cameraMarks, cameraVideo } from '../live/camerahelp';
import { CAMERA_HELP_OFFERED } from '../data/camerahelp';
import { loadConstellations, loadDeepSky, loadStars } from '../data/catalog';
import { MeteorService } from '../live/meteors';
import { ReliveMarks } from '../live/relive';
import { SatelliteService } from '../live/satellites';
import { SmallBodyService } from '../live/smallbodies';
import { attachControls } from '../render/controls';
import { skyDragged } from './pointing';
import { SkyEngine } from '../render/engine';
import * as state from '../state';
import { hideToast, selection, settings, showToast, surveyShown, timeLive, viewMode } from '../state';
import { keptCorrection, watchAlignment } from '../live/align';
import { longName } from '../live/satellites';
import { simbadNear } from '../live/simbad';
import { enuToAltAz, unrefractEnu, vecToRaDec } from '../lib/math';

/** The SIMBAD lookup in flight and its "Looking it up…" note, so any newer tap cancels both. */
let simbadAbort: AbortController | null = null;
let simbadNote = 0;
/** The sky photographs are on the screen and wanted: Full sky, photographs on, and drawn in the last frame. */
const photosUsable = () => !settings.peek().nakedEye && settings.peek().survey && surveyShown.peek();

export function SkyView({ onError }: { onError: (e: Error) => void }) {
  const glRef = useRef<HTMLCanvasElement>(null);
  const ovRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let engine: SkyEngine;
    try {
      engine = new SkyEngine(glRef.current!, ovRef.current!);
    } catch (e) {
      onError(e as Error);
      return;
    }
    const debug: Record<string, unknown> = { engine, state, cameraHelp: cameraHelpDebug };
    (window as unknown as { __ahillah: unknown }).__ahillah = debug;
    engine.onError = onError;
    engine.orientation.calibration = keptCorrection();
    // Tonight's correction stays in force only while it is kept for the night and the place (src/live/align.ts).
    const unwatchAlignment = watchAlignment({ engine } as never);
    const onResize = () => engine.resize();
    onResize();
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    const onVisible = () => {
      if (document.visibilityState === 'visible') engine.orientation.reseed();
    };
    document.addEventListener('visibilitychange', onVisible);
    attachControls(engine, ovRef.current!, {
      onTap: (x, y) => {
        simbadAbort?.abort();
        hideToast(simbadNote);
        // With something selected, a tap on empty sky just clears it.
        const hadSelection = !!selection.peek();
        const hit = engine.pick(x, y, 26, !hadSelection);
        // Zoomed in on the sky photographs, a tap on something we hold nothing for asks SIMBAD: only while they are on
        // the screen (never in Naked eye, which shows none), not merely switched on.
        const cam = engine.camera;
        if (!hadSelection && (!hit || hit.sel.kind === 'constellation' || hit.sel.kind === 'figure') && cam.fovDeg < 8 && photosUsable() && navigator.onLine !== false) {
          const ac = (simbadAbort = new AbortController());
          // The sky is drawn as the air bends it; SIMBAD wants the true position.
          const { ra, dec } = vecToRaDec(engine.frame.toEqj(unrefractEnu(cam.unproject(x, y))));
          const radius = (26 * cam.fovDeg) / Math.min(cam.width, cam.height);
          simbadNote = showToast('Looking it up…', 12000);
          // Nothing found (or no answer): say so, and fall back to the constellation or figure tapped.
          const before = selection.peek();
          const done = (o: typeof selection.value, failed = false) => {
            if (ac.signal.aborted) return;
            hideToast(simbadNote);
            if (!photosUsable()) return; // the photographs have gone meanwhile (Naked eye chosen, or zoomed out)
            if (selection.peek() !== before) return; // something else was chosen meanwhile (search, a card)
            selection.value = o ?? (hit ? hit.sel : null);
            if (!o) showToast(failed ? 'Could not reach SIMBAD just now' : 'SIMBAD lists nothing just there', 2500);
          };
          void simbadNear(ra, dec, radius, ac.signal)
            .then((o) => done(o))
            .catch(() => done(null, true));
          return;
        }
        selection.value = hit ? hit.sel : null;
      },
      onPan: skyDragged,
    });
    Promise.all([loadStars(), loadConstellations(), loadDeepSky()])
      .then(([stars, cons, dsos]) => engine.load({ stars, cons, dsos }))
      .then(() => {
        const sats = new SatelliteService();
        const meteors = new MeteorService();
        const small = new SmallBodyService();
        engine.providers.push(sats, meteors, small, new ReliveMarks(), cameraMarks);
        void small.load();
        sats.onVisible = (id, mag, enu) => {
          const set = settings.peek();
          if (!set.alerts || !timeLive.peek()) return;
          const meta = sats.meta.get(id);
          if (!meta) return;
          const { alt, az } = enuToAltAz(enu);
          const dir = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'][Math.round(az / 45) % 8];
          showToast(`${longName(meta)} is visible now: ${Math.round(alt)}° up in the ${dir}, magnitude ${mag.toFixed(1)}`, 7000);
          if (set.alertVibrate) navigator.vibrate?.([120, 80, 120]); // Android; iPhones do not let websites vibrate
        };
        Object.assign(debug, { sats, meteors, small });
        void sats.loadData(settings.peek().satellites === 'all' ? 'all' : 'bright');
        void meteors.load();
        engine.start();
        services.value = { engine, sats, meteors, small };
        // Opened from a shared link: show that exact sky.
        applyUrlState(services.value);
      })
      .catch(onError);
    // A lookup still on its way when the photographs go (Naked eye chosen, photographs off, zoomed out) is dropped,
    // so it cannot come back later over another view.
    const dropLookup = effect(() => {
      const s = settings.value;
      if ((s.nakedEye || !s.survey || !surveyShown.value) && simbadAbort) {
        simbadAbort.abort();
        simbadAbort = null;
        hideToast(simbadNote);
      }
    });
    return () => {
      dropLookup();
      unwatchAlignment();
      engine.stop();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // The rear camera in pointing mode, for the camera behind the sky and for camera help (src/live/camerahelp.ts): one
  // stream for both, which camera help reads from this video (under the sky, which hides it unless the camera is to be
  // shown). One request at a time: when it settles it looks again at what is wanted by then, so a camera granted after
  // Explore, both switched off or closing is stopped at once, never shown or read.
  useEffect(() => {
    let stream: MediaStream | null = null;
    let asking = false;
    let closed = false;
    /** Waiting for Ahillah to be in view again, to ask for a camera taken away meanwhile. */
    let backInView: (() => void) | null = null;
    const stop = (s: MediaStream) => s.getTracks().forEach((t) => t.stop());
    // Camera help wants it only once it can look (dark, and the sky here and now): it says when that changes (camHelp).
    const wanted = () => !closed && viewMode.peek() === 'ar' && (settings.peek().cameraFeed || (CAMERA_HELP_OFFERED && settings.peek().cameraHelp && cameraHelpCanLook()));
    /** Every track stopped and the video emptied: nothing more is read. */
    const drop = () => {
      if (stream) stop(stream);
      stream = null;
      cameraVideo(null);
      if (videoRef.current) videoRef.current.srcObject = null;
    };
    /** Asked for again once Ahillah is in view: never from the background, where it could be refused, or open the camera
     *  unseen. */
    const whenInView = () => {
      backInView ??= () => {
        if (document.visibilityState === 'hidden') return;
        document.removeEventListener('visibilitychange', backInView!);
        backInView = null;
        void sync();
      };
      document.addEventListener('visibilitychange', backInView);
    };
    const sync = async () => {
      const v = videoRef.current;
      if (!v || asking) return;
      if (!wanted()) return void (stream && drop());
      if (stream) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return whenInView();
      asking = true;
      let s: MediaStream;
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
      } catch (e) {
        asking = false;
        // A refusal that arrives after Explore or with both switched off changes nothing: the camera is not wanted now.
        // Otherwise whatever asked for it goes off, and says so.
        if (wanted()) {
          const set = settings.peek();
          if (set.cameraFeed) state.showToast('Camera not available, showing the sky only.');
          else state.showToast(CAMERA_HELP.refused[refusal(e)], 6000);
          settings.value = { ...set, cameraFeed: false, cameraHelp: false };
        }
        return;
      }
      asking = false;
      if (!wanted()) return stop(s);
      stream = s;
      // Taken away while still wanted (by another app, say): let go, and ask again (once Ahillah is in view).
      (s.getVideoTracks?.() ?? []).forEach((t) => t.addEventListener?.('ended', () => {
        if (stream !== s) return;
        drop();
        void sync();
      }));
      v.srcObject = s;
      await v.play().catch(() => {});
      if (stream === s) cameraVideo(v);
    };
    const a = viewMode.subscribe(() => void sync());
    const b = settings.subscribe(() => void sync());
    const c = camHelp.subscribe(() => void sync());
    return () => {
      closed = true;
      a();
      b();
      c();
      if (backInView) document.removeEventListener('visibilitychange', backInView);
      drop();
    };
  }, []);

  return (
    <div class="sky" role="img" aria-label="The sky. Tap or search to find out what is in it.">
      <video ref={videoRef} class="sky-video" playsInline muted autoPlay aria-hidden="true" />
      <canvas ref={glRef} class="sky-gl" aria-hidden="true" />
      <canvas ref={ovRef} class="sky-overlay" aria-hidden="true" />
    </div>
  );
}
